import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Flag,
  Package,
  Search,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Money } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/products/")({
  component: ProductsModeration,
});

const kpis = [
  { key: "total", value: "1,284", icon: Package, tone: "text-primary" },
  { key: "pending", value: "37", icon: ShieldAlert, tone: "text-amber-600" },
  { key: "approved", value: "1,210", icon: ShieldCheck, tone: "text-emerald-600" },
  { key: "flagged", value: "18", icon: Flag, tone: "text-rose-600" },
  { key: "rejected", value: "19", icon: X, tone: "text-muted-foreground" },
] as const;

type ProductStatus = "Pending" | "Flagged" | "Rejected" | "Approved";

type Product = {
  id: string;
  merchantKey: "amber" | "sakura" | "serenity" | "marina" | "zen" | "grill";
  merchant: string;
  categoryKey: "food" | "beverages" | "wellness" | "experiences";
  price: React.ReactNode;
  submitted: string;
  status?: ProductStatus;
};

const statusStyles: Record<ProductStatus, string> = {
  Pending: "border-amber-500/30 bg-amber-500/10 text-amber-700",
  Flagged: "border-orange-500/30 bg-orange-500/10 text-orange-700",
  Rejected: "border-rose-500/30 bg-rose-500/10 text-rose-700",
  Approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700",
};

const products: Product[] = [
  {
    id: "p-1",
    merchantKey: "amber",
    merchant: "Amber Lounge",
    categoryKey: "beverages",
    price: <Money value="28" />,
    submitted: "Jun 12, 2025",
  },
  {
    id: "p-2",
    merchantKey: "sakura",
    merchant: "Sakura Kitchen",
    categoryKey: "food",
    price: <Money value="185" />,
    submitted: "Jun 11, 2025",
  },
  {
    id: "p-3",
    merchantKey: "serenity",
    merchant: "Serenity Spa",
    categoryKey: "wellness",
    price: <Money value="350" />,
    submitted: "Jun 10, 2025",
  },
  {
    id: "p-4",
    merchantKey: "marina",
    merchant: "Marina Sky Terrace",
    categoryKey: "experiences",
    price: <Money value="420" />,
    submitted: "Jun 12, 2025",
  },
  {
    id: "p-5",
    merchantKey: "zen",
    merchant: "Zen Wellness Center",
    categoryKey: "wellness",
    price: <Money value="200" />,
    submitted: "Jun 09, 2025",
  },
  {
    id: "p-6",
    merchantKey: "grill",
    merchant: "The Grill House",
    categoryKey: "food",
    price: <Money value="95" />,
    submitted: "Jun 08, 2025",
  },
];

const tabs = [
  { key: "all", count: 1284 },
  { key: "pending", count: 37 },
  { key: "flagged", count: 18 },
  { key: "rejected", count: 19 },
] as const;

function ProductsModeration() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<(typeof tabs)[number]["key"]>("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const name = (id: string) => t(`adminProducts.items.${id}.name`);
  const subtitle = (id: string) => t(`adminProducts.items.${id}.subtitle`);
  const category = (key: Product["categoryKey"]) => t(`adminProducts.filters.categories.${key}`);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (tab === "pending" && p.status !== "Pending") return false;
      if (tab === "flagged" && p.status !== "Flagged") return false;
      if (tab === "rejected" && p.status !== "Rejected") return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        name(p.id).toLowerCase().includes(q) ||
        p.merchant.toLowerCase().includes(q) ||
        category(p.categoryKey).toLowerCase().includes(q)
      );
    });
  }, [tab, query, t]);

  const allChecked = filtered.length > 0 && filtered.every((p) => selected[p.id]);
  const toggleAll = (v: boolean) => {
    const next = { ...selected };
    filtered.forEach((p) => (next[p.id] = v));
    setSelected(next);
  };

  return (
    <AdminShell title={t("pages.admin.products.title")} subtitle={t("pages.admin.products.subtitle")}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {kpis.map((k) => (
          <Card key={k.key} className="rounded-2xl border-border/60 p-4 shadow-soft">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {t(`adminProducts.kpis.${k.key}.label`)}
              </span>
              <k.icon className={cn("h-4 w-4", k.tone)} />
            </div>
            <div className="mt-2 text-2xl font-bold tracking-tight">{k.value}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{t(`adminProducts.kpis.${k.key}.hint`)}</div>
          </Card>
        ))}
      </div>

      <Card className="mt-6 rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("adminProducts.searchPlaceholder")}
              className="h-10 rounded-xl border-border/70 bg-surface ps-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 rounded-xl bg-surface p-1">
            {tabs.map((item) => {
              const active = tab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => setTab(item.key)}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-medium transition",
                    active ? "bg-[#5958F3] text-white shadow-soft" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t(`adminProducts.tabs.${item.key}`)} ({item.count.toLocaleString()})
                </button>
              );
            })}
          </div>
          <Select defaultValue="all-cat">
            <SelectTrigger className="h-10 w-[140px] rounded-xl border-border/70 bg-surface">
              <SelectValue placeholder={t("adminProducts.filters.category")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-cat">{t("adminProducts.filters.category")}</SelectItem>
              <SelectItem value="food">{t("adminProducts.filters.categories.food")}</SelectItem>
              <SelectItem value="beverages">{t("adminProducts.filters.categories.beverages")}</SelectItem>
              <SelectItem value="wellness">{t("adminProducts.filters.categories.wellness")}</SelectItem>
              <SelectItem value="experiences">{t("adminProducts.filters.categories.experiences")}</SelectItem>
            </SelectContent>
          </Select>
          <Select defaultValue="all-merch">
            <SelectTrigger className="h-10 w-[140px] rounded-xl border-border/70 bg-surface">
              <SelectValue placeholder={t("adminProducts.filters.merchant")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-merch">{t("adminProducts.filters.merchant")}</SelectItem>
              <SelectItem value="amber">{t("adminProducts.filters.merchants.amber")}</SelectItem>
              <SelectItem value="sakura">{t("adminProducts.filters.merchants.sakura")}</SelectItem>
              <SelectItem value="serenity">{t("adminProducts.filters.merchants.serenity")}</SelectItem>
            </SelectContent>
          </Select>
          <Select defaultValue="any-date">
            <SelectTrigger className="h-10 w-[140px] rounded-xl border-border/70 bg-surface">
              <SelectValue placeholder={t("adminProducts.filters.date")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any-date">{t("adminProducts.filters.date")}</SelectItem>
              <SelectItem value="7d">{t("adminProducts.filters.dates.d7")}</SelectItem>
              <SelectItem value="30d">{t("adminProducts.filters.dates.d30")}</SelectItem>
              <SelectItem value="90d">{t("adminProducts.filters.dates.d90")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4">
          <div>
            <h3 className="text-sm font-semibold">{t("adminProducts.listTitle")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("adminProducts.listSubtitle", { products: "1,284", merchants: "186" })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 rounded-lg">
              {t("adminProducts.selectAll")}
            </Button>
            <Button variant="outline" size="sm" className="h-8 rounded-lg">
              {t("adminProducts.columnView")}
            </Button>
          </div>
        </div>

        <div className="mt-3 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border/60 hover:bg-transparent">
                <TableHead className="w-10 text-center">
                  <div className="flex justify-center">
                    <Checkbox checked={allChecked} onCheckedChange={(v) => toggleAll(Boolean(v))} />
                  </div>
                </TableHead>
                <TableHead>{t("adminProducts.columns.product")}</TableHead>
                <TableHead>{t("adminProducts.columns.merchant")}</TableHead>
                <TableHead>{t("adminProducts.columns.category")}</TableHead>
                <TableHead>{t("adminProducts.columns.price")}</TableHead>
                <TableHead>{t("adminProducts.columns.status")}</TableHead>
                <TableHead>{t("adminProducts.columns.submitted")}</TableHead>
                <TableHead className="text-end">{t("adminProducts.columns.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} className="border-border/60">
                  <TableCell className="text-center">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={!!selected[p.id]}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [p.id]: Boolean(v) }))}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                        <Package className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{name(p.id)}</div>
                        <div className="truncate text-xs text-muted-foreground">{subtitle(p.id)}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{p.merchant}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="rounded-full border-border/70 bg-surface text-[11px] font-medium"
                    >
                      {category(p.categoryKey)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm font-semibold">{p.price}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-[11px] font-medium",
                        statusStyles[p.status ?? "Pending"],
                      )}
                    >
                      {t(`adminProducts.statuses.${p.status ?? "Pending"}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.submitted}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        asChild
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-lg"
                        aria-label={t("adminProducts.view")}
                      >
                        <Link to="/admin/products/$id" params={{ id: p.id }}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 p-4">
          <p className="text-xs text-muted-foreground">
            {t("adminProducts.showing", { count: filtered.length, total: "1,284" })}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg">
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </Button>
            {["1", "2", "3", "…", "214"].map((n, i) => (
              <Button
                key={i}
                variant={n === "1" ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-8 min-w-8 rounded-lg px-2 text-xs",
                  n === "1" && "bg-[#5958F3] text-white hover:bg-[#5958F3]/90",
                )}
              >
                {n}
              </Button>
            ))}
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg">
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </Button>
          </div>
        </div>
      </Card>
    </AdminShell>
  );
}
